import React, { lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "./context/ThemeContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Loader2 } from "lucide-react";

// Route prefetch map — call these on hover to preload page chunks
export const routePrefetchMap = {
  '/': () => import("./pages/Home"),
  '/home': () => import("./pages/Home"),
  '/community': () => import("./pages/Community"),
  '/thread': () => import("./pages/Thread"),
  '/profile': () => import("./pages/Profile"),
  '/settings': () => import("./pages/Settings"),
  '/reviews': () => import("./pages/Reviews"),
  '/tracker': () => import("./pages/Tracker"),
  '/job-board': () => import("./pages/JobBoard"),
  '/requests': () => import("./pages/Requests"),
  '/interview-calendar': () => import("./pages/InterviewCalendar"),
  '/refer': () => import("./pages/ReferFriend"),
  '/help': () => import("./pages/Help"),
};

// Lazy load all pages for better initial load performance
const Layout = lazy(() => import("./components/Layout"));
const Home = lazy(() => import("./pages/Home"));
const Thread = lazy(() => import("./pages/Thread"));
const Community = lazy(() => import("./pages/Community"));
const Profile = lazy(() => import("./pages/Profile"));
const Settings = lazy(() => import("./pages/Settings"));
const Landing = lazy(() => import("./pages/Landing"));
const Auth = lazy(() => import("./pages/Auth"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Admin = lazy(() => import("./pages/Admin"));
const Reviews = lazy(() => import("./pages/Reviews"));
const CompanyReview = lazy(() => import("./pages/CompanyReview"));
const Tracker = lazy(() => import("./pages/Tracker"));
const TrackerDetail = lazy(() => import("./pages/TrackerDetail"));
const JobBoard = lazy(() => import("./pages/JobBoard"));
const Hire = lazy(() => import("./pages/Hire"));
const CandidateProfile = lazy(() => import("./pages/CandidateProfile"));
const Requests = lazy(() => import("./pages/Requests"));
const InterviewCalendar = lazy(() => import("./pages/InterviewCalendar"));
const InterviewDetail = lazy(() => import("./pages/InterviewDetail"));
const HiringManagerSignup = lazy(() => import("./pages/HiringManagerSignup"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const RecruiterPrivacy = lazy(() => import("./pages/RecruiterPrivacy"));
const About = lazy(() => import("./pages/About"));
const Careers = lazy(() => import("./pages/Careers"));
const Product = lazy(() => import("./pages/Product"));
const Pricing = lazy(() => import("./pages/Pricing"));
const HiringProduct = lazy(() => import("./pages/HiringProduct"));
const RecruiterLanding = lazy(() => import("./pages/RecruiterLanding"));
const FreeSignup = lazy(() => import("./pages/FreeSignup"));
const EmailActionHandler = lazy(() => import("./pages/EmailActionHandler"));
const HiringCareers = lazy(() => import("./pages/HiringCareers"));
const HiringTerms = lazy(() => import("./pages/HiringTerms"));
const HiringBillingPolicy = lazy(() => import("./pages/HiringBillingPolicy"));
const ReferFriend = lazy(() => import("./pages/ReferFriend"));
const ReferralRedirect = lazy(() => import("./pages/ReferralRedirect"));
const Help = lazy(() => import("./pages/Help"));
const PlatformDirectToInterview = lazy(() => import("./pages/PlatformDirectToInterview"));
const PlatformCandidateProfileSystem = lazy(() => import("./pages/PlatformCandidateProfileSystem"));
const PlatformPipelineAutomation = lazy(() => import("./pages/PlatformPipelineAutomation"));
const PlatformRecruiterCollaboration = lazy(() => import("./pages/PlatformRecruiterCollaboration"));
const PlatformCandidateSharing = lazy(() => import("./pages/PlatformCandidateSharing"));
const HiringSolutions = lazy(() => import("./pages/HiringSolutions"));
const SolutionSmartTargeting = lazy(() => import("./pages/SolutionSmartTargeting"));
const SolutionDynamicProfiles = lazy(() => import("./pages/SolutionDynamicProfiles"));
const SolutionZeroRiskBooking = lazy(() => import("./pages/SolutionZeroRiskBooking"));
const SolutionCommandCenter = lazy(() => import("./pages/SolutionCommandCenter"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-primary)' }}>
    <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#59A696' }} />
  </div>
);

// Check if we're in development mode
const isDevelopment = import.meta.env.DEV || import.meta.env.VITE_APP_ENV === 'development';

const queryClient = new QueryClient();

function AppContent() {
  const { isAuthenticated, loading, onboardingComplete, fetchingOnboardingStatus, isHiringManager, emailVerified, resendVerificationEmail, logout } = useAuth();
  const [verificationSent, setVerificationSent] = React.useState(false);
  const [verificationError, setVerificationError] = React.useState('');

  // Show loading while auth is initializing OR while fetching onboarding status from Firebase
  if (loading || fetchingOnboardingStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#59A696' }} />
      </div>
    );
  }

  // Block access until email is verified
  if (isAuthenticated && !emailVerified) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--bg-primary)' }}>
        <div className="max-w-md w-full text-center p-8 rounded-2xl border shadow-lg" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)' }}>
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(89,166,150,0.1)' }}>
            <Loader2 className="w-8 h-8" style={{ color: '#59A696' }} />
          </div>
          <h1 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Verify your email</h1>
          <p className="mb-6 text-sm" style={{ color: 'var(--text-secondary)' }}>
            We sent a verification link to your email address. Please check your inbox and click the link to activate your account.
          </p>
          {verificationError && (
            <p className="text-sm mb-3 text-red-500">{verificationError}</p>
          )}
          <button
            onClick={async () => {
              setVerificationError('');
              try {
                await resendVerificationEmail();
                setVerificationSent(true);
              } catch (err) {
                if (err.code === 'auth/too-many-requests') {
                  setVerificationError('Too many attempts. Please wait a few minutes and try again.');
                } else {
                  setVerificationError('Failed to send email. Please try again.');
                }
              }
            }}
            className="w-full py-2 px-4 rounded-lg text-white mb-3 text-sm font-medium"
            style={{ backgroundColor: '#59A696' }}
          >
            {verificationSent ? 'Email sent!' : 'Resend verification email'}
          </button>
          <button
            onClick={() => { logout(); window.location.href = '/'; }}
            className="w-full py-2 px-4 rounded-lg text-sm font-medium border"
            style={{ color: 'var(--text-secondary)', borderColor: 'var(--border-color)' }}
          >
            Back to login
          </button>
        </div>
      </div>
    );
  }

  // Hiring managers: only show recruiter routes, never candidate dashboard
  if (isAuthenticated && isHiringManager) {
    return (
      <Router>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/admin" element={<Admin />} />
            <Route path="/candidates" element={<Hire defaultPanel="candidates" />} />
            <Route path="/hm-candidate" element={<Hire defaultPanel="candidates" />} />
            <Route path="/hire" element={<Hire defaultPanel="home" />} />
            <Route path="/hiring-manager" element={<Hire defaultPanel="home" />} />
            <Route path="/hm-home" element={<Hire defaultPanel="home" />} />
            <Route path="/jobs" element={<Hire defaultPanel="jobs" />} />
            <Route path="/interviews" element={<Hire defaultPanel="interviews" />} />
            <Route path="/hm-calendar" element={<Hire defaultPanel="calendar" />} />
            <Route path="/hm-settings" element={<Hire defaultPanel="settings" />} />
            <Route path="/hiring" element={<HiringManagerSignup />} />
            <Route path="/hiring-manager-signup" element={<HiringManagerSignup />} />
            <Route path="/accept-invite/:inviteId" element={<AcceptInvite />} />
            <Route path="/candidate-profile" element={<CandidateProfile />} />
            <Route path="/candidate-profile/:username" element={<CandidateProfile />} />
            <Route path="/interview/:interviewId" element={<InterviewDetail />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/recruiter-privacy" element={<RecruiterPrivacy />} />
            <Route path="/about" element={<About />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/product" element={<Product />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/hiring-product" element={<HiringProduct />} />
            <Route path="/recruiter-landing" element={<RecruiterLanding />} />
            <Route path="/free-signup" element={<FreeSignup />} />
            <Route path="/email-action" element={<EmailActionHandler />} />
            <Route path="/hiring-careers" element={<HiringCareers />} />
            <Route path="/hiring-terms" element={<HiringTerms />} />
            <Route path="/hiring-billing-policy" element={<HiringBillingPolicy />} />
            <Route path="/r/:username/:code" element={<ReferralRedirect />} />
            <Route path="/help" element={<Help />} />
            <Route path="/hiring-platform/direct-to-interview" element={<PlatformDirectToInterview />} />
            <Route path="/hiring-platform/candidate-profile-system" element={<PlatformCandidateProfileSystem />} />
            <Route path="/hiring-platform/pipeline-automation" element={<PlatformPipelineAutomation />} />
            <Route path="/hiring-platform/recruiter-collaboration" element={<PlatformRecruiterCollaboration />} />
            <Route path="/hiring-platform/candidate-sharing" element={<PlatformCandidateSharing />} />
            <Route path="/hiring-solutions" element={<HiringSolutions />} />
            <Route path="/hiring-solutions/smart-targeting" element={<SolutionSmartTargeting />} />
            <Route path="/hiring-solutions/dynamic-profiles" element={<SolutionDynamicProfiles />} />
            <Route path="/hiring-solutions/zero-risk-booking" element={<SolutionZeroRiskBooking />} />
            <Route path="/hiring-solutions/command-center" element={<SolutionCommandCenter />} />
            {/* Any unknown route sends hiring managers to their dashboard */}
            <Route path="*" element={<Hire defaultPanel="home" />} />
          </Routes>
        </Suspense>
      </Router>
    );
  }

  if (!isAuthenticated) {
    return (
      <Router>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/onboarding" element={<Onboarding />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/candidates" element={<Hire defaultPanel="candidates" />} />
            <Route path="/hm-candidate" element={<Hire defaultPanel="candidates" />} />
            <Route path="/hire" element={<Hire defaultPanel="home" />} />
            <Route path="/hiring-manager" element={<Hire defaultPanel="home" />} />
            <Route path="/hm-home" element={<Hire defaultPanel="home" />} />
            <Route path="/jobs" element={<Hire defaultPanel="jobs" />} />
            <Route path="/interviews" element={<Hire defaultPanel="interviews" />} />
            <Route path="/hm-calendar" element={<Hire defaultPanel="calendar" />} />
            <Route path="/hm-settings" element={<Hire defaultPanel="settings" />} />
            <Route path="/hiring" element={<HiringManagerSignup />} />
            <Route path="/hiring-manager-signup" element={<HiringManagerSignup />} />
            <Route path="/accept-invite/:inviteId" element={<AcceptInvite />} />
            <Route path="/candidate-profile" element={<CandidateProfile />} />
            <Route path="/candidate-profile/:username" element={<CandidateProfile />} />
            <Route path="/interview/:interviewId" element={<InterviewDetail />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/recruiter-privacy" element={<RecruiterPrivacy />} />
            <Route path="/about" element={<About />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/product" element={<Product />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/hiring-product" element={<HiringProduct />} />
            <Route path="/recruiter-landing" element={<RecruiterLanding />} />
            <Route path="/free-signup" element={<FreeSignup />} />
            <Route path="/email-action" element={<EmailActionHandler />} />
                        <Route path="/hiring-careers" element={<HiringCareers />} />
            <Route path="/hiring-terms" element={<HiringTerms />} />
            <Route path="/hiring-billing-policy" element={<HiringBillingPolicy />} />
            <Route path="/r/:username/:code" element={<ReferralRedirect />} />
            <Route path="/help" element={<Help />} />
            <Route path="/hiring-platform/direct-to-interview" element={<PlatformDirectToInterview />} />
            <Route path="/hiring-platform/candidate-profile-system" element={<PlatformCandidateProfileSystem />} />
            <Route path="/hiring-platform/pipeline-automation" element={<PlatformPipelineAutomation />} />
            <Route path="/hiring-platform/recruiter-collaboration" element={<PlatformRecruiterCollaboration />} />
            <Route path="/hiring-platform/candidate-sharing" element={<PlatformCandidateSharing />} />
            <Route path="/hiring-solutions" element={<HiringSolutions />} />
            <Route path="/hiring-solutions/smart-targeting" element={<SolutionSmartTargeting />} />
            <Route path="/hiring-solutions/dynamic-profiles" element={<SolutionDynamicProfiles />} />
            <Route path="/hiring-solutions/zero-risk-booking" element={<SolutionZeroRiskBooking />} />
            <Route path="/hiring-solutions/command-center" element={<SolutionCommandCenter />} />
            <Route path="*" element={<Landing />} />
          </Routes>
        </Suspense>
      </Router>
    );
  }

  // If authenticated but hasn't completed onboarding, redirect to onboarding
  if (!onboardingComplete) {
    return (
      <Router>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/admin" element={<Admin />} />
            <Route path="/candidates" element={<Hire defaultPanel="candidates" />} />
            <Route path="/hm-candidate" element={<Hire defaultPanel="candidates" />} />
            <Route path="/hire" element={<Hire defaultPanel="home" />} />
            <Route path="/hiring-manager" element={<Hire defaultPanel="home" />} />
            <Route path="/hm-home" element={<Hire defaultPanel="home" />} />
            <Route path="/jobs" element={<Hire defaultPanel="jobs" />} />
            <Route path="/interviews" element={<Hire defaultPanel="interviews" />} />
            <Route path="/hm-calendar" element={<Hire defaultPanel="calendar" />} />
            <Route path="/hm-settings" element={<Hire defaultPanel="settings" />} />
            <Route path="/hiring" element={<HiringManagerSignup />} />
            <Route path="/hiring-manager-signup" element={<HiringManagerSignup />} />
            <Route path="/accept-invite/:inviteId" element={<AcceptInvite />} />
            <Route path="/candidate-profile/:username" element={<CandidateProfile />} />
            <Route path="/interview/:interviewId" element={<InterviewDetail />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/recruiter-privacy" element={<RecruiterPrivacy />} />
            <Route path="/about" element={<About />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/product" element={<Product />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/hiring-product" element={<HiringProduct />} />
            <Route path="/recruiter-landing" element={<RecruiterLanding />} />
            <Route path="/free-signup" element={<FreeSignup />} />
            <Route path="/email-action" element={<EmailActionHandler />} />
                        <Route path="/hiring-careers" element={<HiringCareers />} />
            <Route path="/hiring-terms" element={<HiringTerms />} />
            <Route path="/hiring-billing-policy" element={<HiringBillingPolicy />} />
            <Route path="/r/:username/:code" element={<ReferralRedirect />} />
            <Route path="/help" element={<Help />} />
            <Route path="/hiring-platform/direct-to-interview" element={<PlatformDirectToInterview />} />
            <Route path="/hiring-platform/candidate-profile-system" element={<PlatformCandidateProfileSystem />} />
            <Route path="/hiring-platform/pipeline-automation" element={<PlatformPipelineAutomation />} />
            <Route path="/hiring-platform/recruiter-collaboration" element={<PlatformRecruiterCollaboration />} />
            <Route path="/hiring-platform/candidate-sharing" element={<PlatformCandidateSharing />} />
            <Route path="/hiring-solutions" element={<HiringSolutions />} />
            <Route path="/hiring-solutions/smart-targeting" element={<SolutionSmartTargeting />} />
            <Route path="/hiring-solutions/dynamic-profiles" element={<SolutionDynamicProfiles />} />
            <Route path="/hiring-solutions/zero-risk-booking" element={<SolutionZeroRiskBooking />} />
            <Route path="/hiring-solutions/command-center" element={<SolutionCommandCenter />} />
            <Route path="*" element={<Onboarding />} />
          </Routes>
        </Suspense>
      </Router>
    );
  }

  return (
    <Router>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route
            path="/"
            element={
              <Layout>
                <Home />
              </Layout>
            }
          />
        <Route
          path="/home"
          element={
            <Layout>
              <Home />
            </Layout>
          }
        />
        <Route
          path="/community"
          element={
            <Layout>
              <Community />
            </Layout>
          }
        />
        <Route
          path="/thread"
          element={
            <Layout>
              <Thread />
            </Layout>
          }
        />
        <Route
          path="/thread/:threadId"
          element={
            <Layout>
              <Thread />
            </Layout>
          }
        />
        <Route
          path="/profile"
          element={
            <Layout>
              <Profile />
            </Layout>
          }
        />
        <Route
          path="/reviews"
          element={
            <Layout>
              <Reviews />
            </Layout>
          }
        />
        <Route
          path="/reviews/:businessId"
          element={
            <Layout>
              <CompanyReview />
            </Layout>
          }
        />
        <Route
          path="/tracker"
          element={
            <Layout>
              <Tracker />
            </Layout>
          }
        />
        <Route
          path="/tracker/:itemId"
          element={
            <Layout>
              <TrackerDetail />
            </Layout>
          }
        />
        <Route
          path="/job-board"
          element={
            <Layout>
              <JobBoard />
            </Layout>
          }
        />
        <Route
          path="/candidates"
          element={<Hire defaultPanel="candidates" />}
        />
        <Route
          path="/hm-candidate"
          element={<Hire defaultPanel="candidates" />}
        />
        <Route
          path="/candid"
          element={<Hire defaultPanel="candidates" />}
        />
        <Route
          path="/hire"
          element={<Hire defaultPanel="home" />}
        />
        <Route
          path="/hiring-manager"
          element={<Hire defaultPanel="home" />}
        />
        <Route
          path="/hm-home"
          element={<Hire defaultPanel="home" />}
        />
        <Route
          path="/jobs"
          element={<Hire defaultPanel="jobs" />}
        />
        <Route
          path="/interviews"
          element={<Hire defaultPanel="interviews" />}
        />
        <Route
          path="/hm-calendar"
          element={<Hire defaultPanel="calendar" />}
        />
        <Route
          path="/hm-settings"
          element={<Hire defaultPanel="settings" />}
        />
        <Route
          path="/candidate-profile/:username"
          element={<CandidateProfile />}
        />
        <Route
          path="/hiring"
          element={<HiringManagerSignup />}
        />
        <Route
          path="/hiring-manager-signup"
          element={<HiringManagerSignup />}
        />
        <Route
          path="/accept-invite/:inviteId"
          element={<AcceptInvite />}
        />
        <Route
          path="/interview/:interviewId"
          element={<InterviewDetail />}
        />
        <Route
          path="/settings"
          element={
            <Layout>
              <Settings />
            </Layout>
          }
        />
        <Route
          path="/help"
          element={
            <Layout>
              <Help />
            </Layout>
          }
        />
        <Route
          path="/refer"
          element={
            <Layout>
              <ReferFriend />
            </Layout>
          }
        />
        {/* Requests route */}
        <Route
          path="/requests"
          element={
            <Layout>
              <Requests />
            </Layout>
          }
        />
        {/* Interview Calendar route */}
        <Route
          path="/interview-calendar"
          element={
            <Layout>
              <InterviewCalendar />
            </Layout>
          }
        />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/recruiter-privacy" element={<RecruiterPrivacy />} />
        <Route path="/about" element={<About />} />
        <Route path="/careers" element={<Careers />} />
        <Route path="/product" element={<Product />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/hiring-product" element={<HiringProduct />} />
        <Route path="/recruiter-landing" element={<RecruiterLanding />} />
        <Route path="/free-signup" element={<FreeSignup />} />
        <Route path="/email-action" element={<EmailActionHandler />} />
                <Route path="/hiring-careers" element={<HiringCareers />} />
        <Route path="/hiring-terms" element={<HiringTerms />} />
        <Route path="/hiring-billing-policy" element={<HiringBillingPolicy />} />
        <Route path="/r/:username/:code" element={<ReferralRedirect />} />
        <Route path="/hiring-platform/direct-to-interview" element={<PlatformDirectToInterview />} />
        <Route path="/hiring-platform/candidate-profile-system" element={<PlatformCandidateProfileSystem />} />
        <Route path="/hiring-platform/pipeline-automation" element={<PlatformPipelineAutomation />} />
        <Route path="/hiring-platform/recruiter-collaboration" element={<PlatformRecruiterCollaboration />} />
        <Route path="/hiring-platform/candidate-sharing" element={<PlatformCandidateSharing />} />
        <Route path="/hiring-solutions" element={<HiringSolutions />} />
        <Route path="/hiring-solutions/smart-targeting" element={<SolutionSmartTargeting />} />
        <Route path="/hiring-solutions/dynamic-profiles" element={<SolutionDynamicProfiles />} />
        <Route path="/hiring-solutions/zero-risk-booking" element={<SolutionZeroRiskBooking />} />
        <Route path="/hiring-solutions/command-center" element={<SolutionCommandCenter />} />
        </Routes>
      </Suspense>
    </Router>
  );
}

function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
